import { router } from '$lib/router/router.svelte';
import { clearError, setError, setLoading } from '$lib/state/ui.svelte';
import { showSuccess, showError } from '$lib/state/toast.svelte';
import { fetchProjectById } from '$lib/queries/cache';
import {
	exportProjectBOMXLS,
	exportProjectBOQJSON,
	exportProjectBOQCSV,
	sendProjectToQuote,
	getErrorMessage
} from '$lib/api';

function formattedDate() {
	const date = new Date();
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${day}-${month}-${year}`;
}

function triggerDownload(url: string, filename: string) {
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	a.click();
	URL.revokeObjectURL(url);
}

async function projectFromRoute() {
	const projectId = router.projectId;
	if (!projectId) return null;
	return fetchProjectById(projectId);
}

export async function downloadProjectBOMXLSX() {
	const project = await projectFromRoute();
	if (!project) return;

	setLoading(true);
	clearError();
	try {
		const url = await exportProjectBOMXLS(project.id);
		triggerDownload(url, `${project.name}-BOM-${formattedDate()}.xlsx`);
		showSuccess('BOM spreadsheet downloaded');
	} catch (error) {
		setError(getErrorMessage(error));
	} finally {
		setLoading(false);
	}
}

export async function downloadProjectBOQJSON() {
	const project = await projectFromRoute();
	if (!project) return;

	setLoading(true);
	clearError();
	try {
		const data = await exportProjectBOQJSON(project.id);
		const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
		const url = URL.createObjectURL(blob);
		triggerDownload(url, `${project.name}-BOQ-${formattedDate()}.json`);
		showSuccess('BOQ JSON downloaded');
	} catch (error) {
		setError(getErrorMessage(error));
	} finally {
		setLoading(false);
	}
}

export async function downloadProjectBOQCSV() {
	const project = await projectFromRoute();
	if (!project) return;

	setLoading(true);
	clearError();
	try {
		const url = await exportProjectBOQCSV(project.id);
		triggerDownload(url, `${project.name}-BOQ-${formattedDate()}.csv`);
		showSuccess('BOQ CSV downloaded');
	} catch (error) {
		setError(getErrorMessage(error));
	} finally {
		setLoading(false);
	}
}

/** @returns {Promise<{ result: any, panel: { unresolved: string[], created_products: string[], warning: string | null } | null }>} */
export async function sendProjectToProQuote() {
	const project = await projectFromRoute();
	if (!project) return { result: null, panel: null };

	setLoading(true);
	clearError();
	try {
		const result = await sendProjectToQuote(project.id);
		const unresolved = result?.unresolved ?? [];
		const created = result?.created_products ?? [];
		const warning = result?.warning ?? null;

		if (result?.url) {
			showSuccess(`Draft quotation created — open in ProQuote`);
			window.open(result.url, '_blank', 'noopener,noreferrer');
		} else {
			showSuccess('Draft quotation created in ProQuote');
		}

		const panel =
			unresolved.length || created.length || warning
				? { unresolved, created_products: created, warning }
				: null;

		return { result, panel };
	} catch (error) {
		showError(getErrorMessage(error) || 'Failed to send to ProQuote');
		throw error;
	} finally {
		setLoading(false);
	}
}
